import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore, collection, query, orderBy, limit as fblimit,
  onSnapshot, doc, getCountFromServer
} from "firebase/firestore";
import "../../css/InfoBanner.css";

const normalizeHandle = (h) => (!h ? "@unknown" : h.startsWith("@") ? h : `@${h}`);
const formatMoney = (n = 0) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
function timeAgo(ms) {
  if (!ms) return "just now";
  const secs = Math.floor((Date.now() - ms) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const steps = [
    ["year", 31536000], ["month", 2592000], ["week", 604800],
    ["day", 86400], ["hour", 3600], ["minute", 60]
  ];
  for (const [unit, s] of steps) if (secs >= s) return rtf.format(-Math.floor(secs / s), unit);
  return "just now";
}

export default function InfoBanner({
  intervalMs = 4000,
  storageKey = "infoBannerDismissed",
}) {
  const [dismissed, setDismissed] = useState(false);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // 🔴 live data
  const [latest, setLatest] = useState(null);          // {handle, amount, createdAtMs}
  const [totalClaimed, setTotalClaimed] = useState(0); // number
  const [invoiceCount, setInvoiceCount] = useState(0); // integer

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // restore dismissed state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === "1") setDismissed(true);
    } catch {}
  }, [storageKey]);

  // 🔴 Subscribe to Firestore
  useEffect(() => {
    const db = getFirestore();

    // Latest submission
    const latestQ = query(
      collection(db, "submissions"),
      orderBy("createdAt", "desc"),
      fblimit(1)
    );
    const unsubLatest = onSnapshot(latestQ, (snap) => {
      const d = snap.docs[0];
      if (!d) return;
      const x = d.data();
      setLatest({
        handle: normalizeHandle(x.handle),
        amount: Number(x.amountCents ?? x.amount ?? 0),
        createdAtMs: x.createdAt?.toMillis?.() ?? 0,
      });
    });

    // Stats doc for total + count
    const unsubStats = onSnapshot(doc(db, "stats", "global"), async (snap) => {
      const data = snap.data() || {};
      if (typeof data.total === "number") setTotalClaimed(data.total);
      if (typeof data.count === "number") {
        setInvoiceCount(data.count);
      } else {
        // fallback: get count once
        try {
          const agg = await getCountFromServer(collection(db, "submissions"));
          setInvoiceCount(agg.data().count || 0);
        } catch {}
      }
    });

    return () => { unsubLatest(); unsubStats(); };
  }, []);

  // Compose messages
  const items = useMemo(() => {
    const arr = [];
    if (totalClaimed > 0 || invoiceCount > 0) {
      arr.push({
        id: "totals",
        text: `TOTAL CLAIMED: ${formatMoney(totalClaimed)} from ${invoiceCount.toLocaleString()} Invoices`
      });
    }
    if (latest) {
      arr.push({
        id: "latest",
        text: `${latest.handle} just claimed ${formatMoney(latest.amount)} — ${timeAgo(latest.createdAtMs)}`
      });
    }
    return arr;
  }, [latest, totalClaimed, invoiceCount]);

  // rotate items
  useEffect(() => {
    if (dismissed || paused || items.length <= 1) return;
    if (reducedMotion) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % items.length), intervalMs);
    return () => clearInterval(t);
  }, [dismissed, paused, items.length, intervalMs, reducedMotion]);

  const onClose = () => {
    setDismissed(true);
    try { localStorage.setItem(storageKey, "1"); } catch {}
  };

  if (dismissed || items.length === 0) return null;
  const current = items[index];

  return (
    <div
      className="infobanner"
      role="region"
      aria-live="polite"
      aria-label="Updates"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="infobanner-inner">
        <div className="infobanner-viewport">
          <div key={current.id} className="infobanner-item">
            {current.text}
          </div>
        </div>
        <button
          className="infobanner-close"
          onClick={onClose}
          aria-label="Dismiss updates"
          title="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
